import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Box, CircularProgress, Fab, FabProps, Stack, styled, Typography } from '@mui/material'

import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { FAQItem } from './FAQItem'
import { FAQItemData, loadFAQs } from './faqUtils'

// Floating action button
const FAQButton = styled(Fab)<FabProps>(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.05)',
  },
  backgroundColor: theme.palette.primary.main,
  bottom: theme.spacing(3),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
  color: theme.palette.primary.contrastText,
  height: 56,
  position: 'fixed',
  right: theme.spacing(3),
  transition: 'transform 0.2s ease, background-color 0.2s ease',
  width: 56,
  zIndex: 10000,
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(10),
    height: 52,
    right: theme.spacing(2),
    width: 52,
  },
}))

// Panel container
const FAQPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  backgroundColor: theme.palette.grey[900],
  borderRadius: theme.spacing(1.5),
  bottom: theme.spacing(11),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: isOpen ? '70vh' : 0,
  opacity: isOpen ? 1 : 0,
  overflow: 'hidden',
  pointerEvents: isOpen ? 'auto' : 'none',
  position: 'fixed',
  right: theme.spacing(3),
  transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  width: 400,
  zIndex: 9999,
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(18),
    left: theme.spacing(2),
    maxHeight: isOpen ? '55vh' : 0,
    right: theme.spacing(2),
    width: 'auto',
  },
}))

const PanelHeader = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[800]}`,
  flexShrink: 0,
  padding: theme.spacing(2.5),
  paddingBottom: theme.spacing(2),
}))

const PanelContent = styled(Box)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[700],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.grey[600],
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(1),
}))

const LoadingContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
}))

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  padding: theme.spacing(2),
  textAlign: 'center',
}))

const EmptyMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
  textAlign: 'center',
}))

export const FAQWidget: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [faqs, setFaqs] = useState<FAQItemData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)

  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Toggle panel visibility
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  // Toggle FAQ item expansion
  const handleItemToggle = useCallback((index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index))
  }, [])

  // Fetch FAQs on first open only
  useEffect(() => {
    if (isOpen && !hasLoaded) {
      setIsLoading(true)
      setError(null)

      loadFAQs()
        .then((data) => {
          setFaqs(data)
          setHasLoaded(true)
        })
        .catch((err) => {
          console.error('Error loading FAQs:', err)
          setError('Unable to load FAQs. Please try again later.')
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [isOpen, hasLoaded])

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (
        isOpen &&
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(target) &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  return (
    <>
      <FAQPanel ref={panelRef} isOpen={isOpen} role="dialog" aria-label="Frequently Asked Questions">
        <PanelHeader>
          <Typography component="h2" fontWeight={700} letterSpacing={0} variant="h6">
            FAQ
          </Typography>
          <Typography color="text.secondary" letterSpacing={0} variant="body2">
            Find answers to common questions
          </Typography>
        </PanelHeader>

        <PanelContent>
          {isLoading && (
            <LoadingContainer>
              <CircularProgress size={32} />
              <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
                Loading FAQs...
              </Typography>
            </LoadingContainer>
          )}

          {error && <ErrorMessage variant="body2">{error}</ErrorMessage>}

          {!isLoading && !error && faqs.length === 0 && hasLoaded && (
            <EmptyMessage variant="body2">No FAQs available.</EmptyMessage>
          )}

          {!isLoading &&
            !error &&
            faqs.map((faq, index) => (
              <FAQItem
                key={index}
                answer={faq.answer}
                isExpanded={expandedIndex === index}
                onToggle={() => handleItemToggle(index)}
                question={faq.question}
              />
            ))}
        </PanelContent>
      </FAQPanel>

      <FAQButton
        ref={buttonRef}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close FAQ' : 'Open FAQ'}
        onClick={handleToggle}
      >
        <HelpOutlineIcon />
      </FAQButton>
    </>
  )
}
FAQWidget.displayName = 'FAQWidget'
